/**
 * SummaryCardStrip — read-only at-a-glance panel rendered at the top of a
 * record (08_summary_card.md §8.5). Pulls admin-configured fields from across
 * the record's form submissions. Not clickable (§8.5.4). Renders nothing when
 * the project has no summary config, so it stays out of the way until set up.
 */
import { useState } from 'react'
import { AlertTriangle, Clock, ChevronDown, ChevronUp } from 'lucide-react'
import { DynamicIcon } from '@/lib/utils/lucide-icon'
import { cn } from '@/lib/utils/cn'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useSummaryCardQuery } from '../api/use-summary-card-query'

dayjs.extend(relativeTime)

const COLLAPSE_THRESHOLD = 8

export interface SummaryCardStripProps {
  projectId: string
  recordId: string
}

export function SummaryCardStrip({ projectId, recordId }: SummaryCardStripProps) {
  const { data: items, isLoading } = useSummaryCardQuery(projectId, recordId)
  const [expanded, setExpanded] = useState(false)

  if (isLoading) {
    return (
      <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-9 bg-muted rounded-md animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  // No config (or nothing resolved) → render nothing.
  if (!items || items.length === 0) return null

  const isCollapsible = items.length > COLLAPSE_THRESHOLD
  const visible = isCollapsible && !expanded ? items.slice(0, COLLAPSE_THRESHOLD) : items

  return (
    <div className="shrink-0 border-b border-border bg-muted/20 px-4 py-3">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
        {visible.map((item) => (
          <div key={item.id} className="min-w-0">
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              {item.icon && <DynamicIcon name={item.icon} className="w-3 h-3 shrink-0" />}
              <span className="truncate">{item.label}</span>
              {item.hasWarning && (
                <AlertTriangle
                  className="w-3 h-3 text-yellow-400 shrink-0"
                  aria-label="دارای هشدار بالینی"
                />
              )}
            </div>
            <div
              className={cn(
                'text-sm font-medium truncate',
                item.isEmpty ? 'text-muted-foreground/60 italic' : 'text-foreground',
              )}
              title={item.value}
            >
              {item.value}
            </div>
            {item.lastUpdated && (
              <div
                className={cn(
                  'flex items-center gap-0.5 text-[9px]',
                  item.isStale ? 'text-yellow-500/80' : 'text-muted-foreground/70',
                )}
              >
                {item.isStale && <Clock className="w-2.5 h-2.5" />}
                به‌روزشده {dayjs(item.lastUpdated).fromNow()}
              </div>
            )}
          </div>
        ))}
      </div>

      {isCollapsible && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              نمایش کمتر
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              نمایش {(items.length - COLLAPSE_THRESHOLD).toLocaleString('fa-IR')} مورد دیگر
            </>
          )}
        </button>
      )}
    </div>
  )
}
